/* eslint-disable max-len */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { modeUIContext, showErrorNotification } from '@moderepo/mode-ui-state';
import { Button, Checkbox, CircularProgress, Divider, Grid, Icon, ListItemIcon, makeStyles, Menu, MenuItem, Paper, Theme, Typography }
    from '@material-ui/core';
import { ApiError, EntityModuleType, SortOrder } from '@moderepo/mode-internal-apis';
import { EventLogData, EventLogRecord, ModeAppProxyAPI } from '@moderepo/mode-apis';
import { capitalizeAll, compareTimestamps, GenericMessageBox, getErrorMessage, ITEMS_PER_PAGE_10, useDropdown, SELECT_INPUT_OPTION_DIVIDER,
    SELECT_INPUT_OPTION_DIVIDER_TYPE } from '@moderepo/mode-app-components';
import produce, { castDraft, Draft } from 'immer';
import clsx from 'clsx';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingStringValue, isValidSettingNumberValue, isEmptyObject } from '../../../utils/utils';
import { EntityUIComponentCollection, EntityUIComponentProps, filterMetricsDefinitionsByMetricsExclusionCustomSettings, getMetricsDefinitionMetricsId,
    GridCustomSettings, isGridCustomSettings, isMetricsDefinitionAndMetricsExclusionCustomSettings,
    MetricsDefinitionsAndMetricsExclusionCustomSettings, useSelectTranslatedEntityClasses } from '..';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { useRitzTranslation } from '../../../utils/customHooks';
import { ExtGrid } from '../..';
import { EntityEventViewComponentCollection, EventFilterOption, isEventFilterOption,
    useConvertTimeControlParamsToFetchDataTimeRangeParams } from '.';
import { SettingsErrorLogger } from '../../../utils/logger';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        controlBar: {
            padding      : theme.spacing(1),
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
            marginBottom : theme.spacing(1),
        },

        filterOption: {
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
        },

        filterOptionFieldLabel: {
            textTransform: 'capitalize',
            marginRight  : theme.spacing(1),
        },

        filterOptionToggler: {
            textTransform: 'none',
            borderBottom : `1px solid ${theme.palette.text.secondary}`,
        },

        filterOptionLabel: {
            textTransform: 'capitalize',
        },

        loadMoreButtonContainer: {
            width         : '100%',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            marginTop     : theme.spacing(3),
        },

        loadMoreButton: {
        },


        eventRecordViewWrapper: {
        },

        eventRecordView: {
            height: '100%',
        },

        genericEventRecordViewContent: {
            display   : 'block',
            width     : '100%',
            height    : '100%',
            minHeight : 200,
            overflow  : 'auto',
            margin    : 0,
            resize    : 'vertical',
            whiteSpace: 'pre',
        },

        [theme.breakpoints.up('md')]: {
            controlBar: {
                marginBottom: theme.spacing(2),
            },
        },

        [theme.breakpoints.up('lg')]: {
            controlBar: {
                marginBottom: theme.spacing(3),
            },
        },
    };
}, { name: 'EntityEventsUIComponent', index: 1 });



/**
 * The data structure for the fetch metrics definitions response data. Since fetch data for multiple metrics definition, the result will
 * be a MAP of metricsDefinitionId => EventLogData. This "type" definition is just for convenience so that we don't have to repeat this
 * structure multiple times.
 */
interface MultipleFetchEventLogResponseData {
    [metricsDefId: string]: EventLogData | undefined;
}


/**
 * The data structure for each event record we will be displaying in the UI. We will need to keep the "metricsDefId" in the EventRecordInfo
 * so that we determine how to display the record based on the metricsDefinitionId. Different event records from different metrics definition
 * will be displayed differently.
 */
interface EventRecordInfo {
    readonly metricsDefId: string;
    readonly record: EventLogRecord;
}



/**
 * Fetch event logs for multiple metricsDefinitions based on the specified date/time and pagination params.
 * Return a map of event logs array by metricsDefinitionId.
 */
const fetchEventLogs = async (
    homeId: number,
    metricsDefinitionsInfo: readonly {
        readonly metricsDefinitionId: string;
        readonly metricsDefinitionMetricsId: string;
        readonly logResourcePath: string;
    }[],
    startTime: number,
    endTime: number,
    limit: number,
    skip: number,
    totalByMetricsDefId: {
        readonly [metricsDefId: string]: number | undefined;
    },
    sortOrder: SortOrder,
    filters: readonly EventFilterOption[],
): Promise<MultipleFetchEventLogResponseData> => {

    // Create an array of fetch data requests and add all the requests into this array
    const requests: (Promise<EventLogData> | undefined)[] = [];

    metricsDefinitionsInfo.forEach((metricsDefInfo) => {
        // Get the pagination state for the current metrics definition
        const total = totalByMetricsDefId[metricsDefInfo.metricsDefinitionId];

        // Only make API call if we have not at the last page
        if (total === undefined || skip < total) {
            // "filters" contains filters for many metrics definition. We only need to use the filters configured for this current
            // metrics definition
            const metricsDefinitionFilters = filters.filter((filter) => {
                return filter.metricsDefinitionId === metricsDefInfo.metricsDefinitionId;
            });

            // If "filters" is empty then it means there is no filters and we can fetch data for all metrics definition
            // HOWEVER, if "filters" is NOT empty BUT the "metricsDefinitionFilters" is empty then it means there are filters selected BUT
            // there isn't any filter selected for this metrics definition. If that is the case then DON'T fetch data for this metrics definition
            if (filters.length === 0 || (filters.length > 0 && metricsDefinitionFilters.length > 0)) {
                requests.push(ModeAppProxyAPI.getInstance().getEventLog(homeId, metricsDefInfo.logResourcePath, {
                    begin       : (new Date(startTime)).toISOString(),
                    end         : (new Date(endTime)).toISOString(),
                    searchKeys  : metricsDefInfo.metricsDefinitionMetricsId,
                    sortBy      : sortOrder,
                    limit,
                    skip,
                    // Combine all the `metricsDefinitionFilters` and wrap it around an "OR" conjunction operator
                    fieldsFilter: metricsDefinitionFilters.length > 0 ? `OR(${metricsDefinitionFilters.map((filterOption) => {
                        return filterOption.value;
                    }).join(',')})` : undefined,
                }));
            } else {
                requests.push(undefined);
            }
        } else {
            requests.push(undefined);
        }
    });

    // Make all the requests in one Promise
    const response = await Promise.allSettled(requests);
    const result: MultipleFetchEventLogResponseData = {};
    response.forEach((response, index) => {
        if (response.status === 'fulfilled') {
            const metricsDef = metricsDefinitionsInfo[index];
            result[metricsDef.metricsDefinitionId] = response.value;
        }
    });

    return result;
};



type EventViewComponentValue = string | {
    readonly componentType: string;
    readonly customSettings?: object | undefined;
}



/**
 * Type guard function to check if the provided obj is an EventViewComponentValue. An EventViewComponentValue can be either this
 *      customSettings: {
 *          eventsSortOrder: 'desc',
 *
 *          // 'eventViewComponent' is just a name of one of the existing view component. Use this if we don't need to customize the view component
 *          eventViewComponent: "Generic_Video_Event_View"
 *      }
 * Or this
 *      customSettings: {
 *          eventsSortOrder: 'desc',
 *
 *          // 'eventViewComponent' is an OBJECT. Use this if we need to customize the view component
 *          eventViewComponent: {
 *              componentType: "Generic_Video_Event_View",
 *              customSettings: {
 *                  showEntityName: false,
 *                  showEventType: true,
 *              }
 *          }
 *      }
 */
const isEventViewComponentValue = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EventViewComponentValue => {

    const settings = obj as EventViewComponentValue;
    const configPath = [...(currentConfigPath ?? [])];

    // Make sure "settings" is a string or object
    if (typeof settings !== 'string' && !isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be a non-empty string or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // Make settings is valid. The event view component type name MUST exist and the customSettings must be valid if it is provided

    // Get the event view component type name. If "eventViewComponent" is a string then it must be the name of the component type.
    // If "eventViewComponent" is an object then the name will come from the "eventViewComponent.componentType" attribute
    const eventViewCompTypeName = typeof settings === 'string' ? settings : settings.componentType;

    // If "eventViewComponent" is an Object then get the "customSettings" from the object, this is OPTIONAL so it can be undefined
    const customSettings = isObjectLiteral(settings) ? settings.customSettings : undefined;

    // Find the entity view component info from the collection based on the eventViewCompTypeName. If we can't find it then that mean
    // the "eventViewCompTypeName" is invalid
    const eventViewComponentInfo = EntityEventViewComponentCollection.getInstance().getComponent(eventViewCompTypeName);

    if (!eventViewComponentInfo) {
        return errorLogger?.logError({
            message: `Value is required and must be one of the following [${
                `'${EntityEventViewComponentCollection.getInstance().getAllComponentTypes().join(', ')}'`
            }]`,
            value: eventViewCompTypeName,
            path : typeof settings === 'string' ? [...configPath] : [...configPath, 'componentType'],
        }) ?? false;
    }

    // customSettings is OPTIONAL but if it is provided AND the entityViewComponentInfo has a validator for the customSettings then call
    // the validator to validate the customSettings.
    // TODO - pass in logger
    if (
        customSettings !== undefined && eventViewComponentInfo.customSettingsValidator !== undefined
            && !eventViewComponentInfo.customSettingsValidator(customSettings, errorLogger, [...configPath, 'customSettings'])
    ) {
        // Failed to validate the customSettings
        return false;
    }

    return true;
};



/**
 * This is the interface for the EntityEventsUIComponent's CustomSettings.
 */
export interface EntityEventsUIComponentCustomSettings extends MetricsDefinitionsAndMetricsExclusionCustomSettings, GridCustomSettings {
    // The custom pagination limit to use when fetching events. The default is 10. The range is any number between 5 and 100.
    readonly fetchEventsLimit?: number | undefined;

    // To set the sort order of the events. The default will be DESC (latest events show first) if this is not provided.
    readonly eventsSortOrder?: SortOrder | undefined;

    /**
     * The component to be used for displaying the events. This will be used for all event type UNLESS a specific component
     * is specified for a metrics definition id. The value for this attribute can be either the name of the component or an Object that
     * has component type AND custom settings
     *
     *  eventViewComponent = 'Generic_Video_Event_View'
     *
     * OR
     *
     *  eventViewComponent = {
     *      componentType: 'Generic_Video_Event_View',
     *      customSettings: {
     *          ...
     *      }
     *  }
     */
    readonly eventViewComponent?: EventViewComponentValue | undefined;

    /**
     * If an Entity Class has events that come from more than 1 metrics definition and the event from different metrics definition has
     * different UI then we can use this to specify which component to use for which event type. E.g.
     *
     *  eventViewComponentPerMetricsDefinition: {
     *      breathalyzer_event: 'Generic_Video_Event_View',
     *      vehicle_event: {
     *          componentType: 'Generic_Video_Event_View',
     *          customSettings: {
     *              ...
     *          }
     *      }
     *  }
     */
    readonly eventViewComponentPerMetricsDefinition?: {
        readonly [metricsDefId: string]: EventViewComponentValue | undefined;
    } | undefined;


    readonly controlBar?: {
        readonly eventsFilter?: {
            // The Array of filter options to show in the Event filter dropdown
            readonly options: readonly (EventFilterOption | SELECT_INPUT_OPTION_DIVIDER_TYPE)[];
        } | undefined;
    } | undefined;
}



/**
 * Type guard function to validate the obj to make sure it is a type of EntityEventsUIComponentCustomSettings
 */
const isEntityEventsUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityEventsUIComponentCustomSettings => {
    const settings = obj as EntityEventsUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.fetchEventsLimit, false, false, 5, 100)) {
        return errorLogger?.logError({
            message: '"fetchEventsLimit" must be undefined or an integer between 5 and 100.',
            value  : settings.fetchEventsLimit,
            path   : [...configPath, 'fetchEventsLimit'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.eventsSortOrder, false, false, Object.values(SortOrder))) {
        return errorLogger?.logError({
            message: `"eventsSortOrder" must be undefined or one of the following values [${Object.values(SortOrder).map((value) => {
                return `"${value}"`;
            }).join(', ')}].`,
            value: settings.eventsSortOrder,
            path : [...configPath, 'eventsSortOrder'],
        }) ?? false;
    }

    // "eventViewComponent" is OPTIONAL but if it is provided, it MUST be a string or an Object
    if (
        settings.eventViewComponent !== undefined
        && !isEventViewComponentValue(settings.eventViewComponent, errorLogger, [...configPath, 'eventViewComponent'])
    ) {
        return false;
    }

    // "eventViewComponentPerMetricsDefinition" is OPTIONAL but if it is provided, it must be valid
    if (settings.eventViewComponentPerMetricsDefinition !== undefined) {
        if (!isObjectLiteral(settings.eventViewComponentPerMetricsDefinition)) {
            return errorLogger?.logError({
                message: '"eventViewComponentPerMetricsDefinition" must be undefined or an object.',
                value  : settings.eventViewComponentPerMetricsDefinition,
                path   : [...configPath, 'eventViewComponentPerMetricsDefinition'],
            }) ?? false;
        }

        if (!Object.entries(settings.eventViewComponentPerMetricsDefinition).every(([metricsDefId, eventViewComponentSettings]) => {
            if (!isEventViewComponentValue(
                eventViewComponentSettings, errorLogger, [...configPath, 'eventViewComponentPerMetricsDefinition', metricsDefId],
            )) {
                return false;
            }
            return true;
        })) {
            return false;
        }
    }

    // Control bar is optional but if it is provided, it must be valid
    if (settings.controlBar !== undefined) {
        if (!isObjectLiteral(settings.controlBar)) {
            return errorLogger?.logError({
                message: '"controlBar" must be undefined or an object.',
                value  : settings.controlBar,
                path   : [...configPath, 'controlBar'],
            }) ?? false;
        }

        if (settings.controlBar.eventsFilter) {
            if (!isObjectLiteral(settings.controlBar.eventsFilter)) {
                return errorLogger?.logError({
                    message: '"eventsFilter" must be undefined or an object.',
                    value  : settings.controlBar.eventsFilter,
                    path   : [...configPath, 'controlBar', 'eventsFilter'],
                }) ?? false;
            }
            if (!(settings.controlBar.eventsFilter.options instanceof Array)) {
                return errorLogger?.logError({
                    message: '"options" must be an Array.',
                    value  : settings.controlBar.eventsFilter.options,
                    path   : [...configPath, 'controlBar', 'eventsFilter', 'options'],
                }) ?? false;
            }
            if (!settings.controlBar.eventsFilter.options.every((option, index) => {
                if (typeof option === 'string') {
                    if (option !== SELECT_INPUT_OPTION_DIVIDER) {
                        return errorLogger?.logError({
                            message: `Option value must be an divider "${SELECT_INPUT_OPTION_DIVIDER}" or an Object`,
                            value  : option,
                            path   : [...configPath, 'controlBar', 'eventsFilter', 'options', `[${index}]`],
                        }) ?? false;
                    }
                    return true;
                }

                if (!isEventFilterOption(option, errorLogger, [...configPath, 'controlBar', 'eventsFilter', 'options', `[${index}]`])) {
                    return false;
                }
                return true;
            })) {
                return false;
            }
        }
    }


    // Because settings is also an MetricsExclusionCustomSettings, we can call this type guard function to validate the props defined
    // for "MetricsExclusionCustomSettings"
    if (!isMetricsDefinitionAndMetricsExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Because settings is also an GridCustomSettings, we can call this type guard function to validate the props defined for "GridCustomSettings"
    if (!isGridCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



export interface EntityEventsUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityEventsUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing an entity's events data
 */
export const EntityEventsUIComponent: React.FC<EntityEventsUIComponentProps> = (props: EntityEventsUIComponentProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const classes = useStyle();
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const [eventFilterMenuAnchorEl, isEventFilterMenuOpened, onEventFilterAnchorElClicked, onEventFilterMenuClosed] = useDropdown();


    /**
     * Fetch data for ALL the entityClasses, the selected entity. Then find the entityClass that the selected entity belongs to and also
     * find the metrics definition from the entity class where we can fetch event data from.
     */
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);

    const selectedEntityClass = useMemo(() => {
        return entityClasses?.find((entityClass) => {
            return entityClass.entityClass === selectedEntity?.entityClass;
        });
    }, [entityClasses, selectedEntity?.entityClass]);


    /**
     * To fetch event logs, we will need these info, homeId, logResourcePath from the metricsDef's settings, and the metricsDefinitionMetricsId.
     * These info will change when the selectedEntity changed so we need to get new value each time the selectedEntity changed
     */
    const { homeId, metricsDefinitionsInfo } = useMemo(() => {
        if (selectedEntity && selectedEntityClass) {

            // filter out metrics definitions based on the customSettings' metrics exclusion settings
            const filteredMetricsDef = filterMetricsDefinitionsByMetricsExclusionCustomSettings(
                selectedEntityClass.metricsDefinitions ?? [], props.customSettings,
            ).filter((metricsDef) => {
                // Then filter out the metrics definitions that are NOT of type HOME_APP_PROXY
                return metricsDef?.dataStoreConfiguration.moduleType === EntityModuleType.HOME_APP_PROXY;
            });


            if (filteredMetricsDef) {
                return {
                    homeId                : selectedEntity.homeId,
                    metricsDefinitionsInfo: filteredMetricsDef.map((metricsDef) => {
                        return {
                            metricsDefinitionMetricsId: getMetricsDefinitionMetricsId(selectedEntity, metricsDef.metricsDefinitionId),
                            logResourcePath           : metricsDef.dataStoreConfiguration.moduleInstanceId,
                            metricsDefinitionId       : metricsDef.metricsDefinitionId,
                        };
                    }),
                };
            }
        }

        return {
            homeId: undefined, metricsDefinitionsInfo: undefined,
        };
    }, [selectedEntity, selectedEntityClass, props.customSettings]);


    /**
     * Create the params that are required for fetching data based on the props.fetchDataParams. Since this component need to fetch data
     * based on some start/end time, it will convert the props.fetchDataParams to FetchDataByTimeRangeOptions.
     */
    const fetchDataOptions = useConvertTimeControlParamsToFetchDataTimeRangeParams(props.fetchDataParams);



    /**
     * The state to keep track of the events data we fetched
     */
    const [fetchedEventsData, setFetchedEventsData] = useState<{
        readonly startTime?: number | undefined;
        readonly endTime?: number | undefined;

        // Used for pagination
        // limit is the same for all metrics definition therefore it doesn't need to be per metrics definition
        readonly limit: number;

        // Skip can be different per metrics definition since each metrics definition can have different number of events. HOWEVER, we will
        // use the same value for ALL metrics definition. Before we fetch data, we will make sure skip < total.
        readonly skip: number;

        // We need to keep track of the total events value per metrics definition since each metrics definition have different total # of events.
        readonly totalByMetricsDefId: {
            readonly [metricsDefId: string]: number | undefined;
        },

        readonly sortOrder: SortOrder;

        // This is where all the fetched events are stored. Each metricsDefinitionId will have an ARRAY of EventLogData, 1 for each page.
        readonly eventsDataByMetricsDefId: {
            readonly [metricsDefId: string]: readonly EventLogData[] | undefined;
        }

        readonly filters: readonly EventFilterOption[];
    }>({
        // Get the limit from the customSettings if there is one. If not, set it to 10.
        limit: props.customSettings?.fetchEventsLimit !== undefined
            ? Math.floor(props.customSettings.fetchEventsLimit) : ITEMS_PER_PAGE_10,
        skip                    : 0,
        totalByMetricsDefId     : {},
        sortOrder               : props.customSettings?.eventsSortOrder ?? SortOrder.DESC,
        eventsDataByMetricsDefId: {},
        filters                 : [],
    });


    /**
     * Event data we fetched are stored in fetchedEventsData and they are separated by metrics definition. To display all events on the same
     * screen, we will combine the event records for all events into a flat array. Also, because events from different metrics definition has
     * different timestamp so after we merged all the event records, we will need to sort them by timestamp.
     */
    const combinedEventsRecords = useMemo(() => {
        const result: EventRecordInfo[] = [];
        Object.entries(fetchedEventsData.eventsDataByMetricsDefId).forEach(([metricsDefId, multipleEventLogData]) => {
            if (multipleEventLogData) {
                multipleEventLogData.forEach((eventLogData) => {
                    (eventLogData.records ?? []).forEach((record) => {
                        result.push({
                            metricsDefId,
                            record,
                        });
                    });
                });
            }
        });

        // Return the records sorted by timestamp
        return result.sort((recordInfo1, recordInfo2) => {
            const compareResult = compareTimestamps(recordInfo1.record.timestamp, recordInfo2.record.timestamp);

            // "compareResult" is the result based on ASCENDING order. So we need to multiply the result with -1 if the sort order is DESC
            return compareResult * (fetchedEventsData.sortOrder === SortOrder.DESC ? -1 : 1);
        });
    }, [fetchedEventsData.eventsDataByMetricsDefId, fetchedEventsData.sortOrder]);



    /**
     * Whenever the fetchDataOptions, selectedEntity changed, we will need to reset the current video event data e.g. currentPage, videos, etc...
     * because it will be a different set of videos.
     */
    useEffect(() => {
        if (metricsDefinitionsInfo && fetchDataOptions) {
            setFetchedEventsData((currentValue) => {
                return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                    draft.startTime = fetchDataOptions.startTime;
                    draft.endTime = fetchDataOptions.endTime;
                    // Only change these to a new {} if these objects are NOT empty. If they are empty, continue using the old object.
                    // This is to prevent the component from re-rendering or hooks from executing again because the state changed
                    draft.totalByMetricsDefId = isEmptyObject(draft.totalByMetricsDefId) ? draft.totalByMetricsDefId : {};
                    draft.eventsDataByMetricsDefId = isEmptyObject(draft.eventsDataByMetricsDefId) ? draft.eventsDataByMetricsDefId : {};
                });
            });
        }
    }, [fetchDataOptions, metricsDefinitionsInfo]);



    /**
     * This useEffect is used for fetching data whenever some of the state changed e.g. startTime, endTime, etc...
     */
    useEffect(() => {
        if (homeId && metricsDefinitionsInfo && fetchedEventsData.startTime !== undefined && fetchedEventsData.endTime !== undefined) {

            // Make API call to fetch event log data for the specified params
            fetchEventLogs(
                homeId,
                metricsDefinitionsInfo,
                fetchedEventsData.startTime,
                fetchedEventsData.endTime,
                fetchedEventsData.limit,
                fetchedEventsData.skip,
                fetchedEventsData.totalByMetricsDefId,
                fetchedEventsData.sortOrder,
                fetchedEventsData.filters,
            ).then((newEventLogDataByMetricsDefId) => {

                setFetchedEventsData((currentData) => {
                    return produce(currentData, (draft: Draft<typeof currentData>) => {
                        Object.entries(newEventLogDataByMetricsDefId).forEach(([metricsDefId, newEventLogData]) => {
                            if (newEventLogData) {
                                const records = draft.eventsDataByMetricsDefId[metricsDefId] ?? [];
                                draft.eventsDataByMetricsDefId[metricsDefId] = records;
                                records.push(castDraft(newEventLogData));
                            }
                        });
                    });
                });
            }).catch((error) => {
                uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
            });
        }
    }, [fetchedEventsData.limit, fetchedEventsData.sortOrder, metricsDefinitionsInfo, homeId, uiDispatch, fetchedEventsData.startTime,
        fetchedEventsData.endTime, fetchedEventsData.totalByMetricsDefId, fetchedEventsData.skip, fetchedEventsData.filters]);



    /**
     * Fetch more events. This will not make any API call. All it does is update the eventsData.pagination's "skip" state for each metrics definition.
     * This will cause the useEffect above to automatically fetch data based on the new "skip" value.
     */
    const fetchMoreEvents = useCallback(() => {
        if (metricsDefinitionsInfo) {
            setFetchedEventsData((currentValue) => {
                return produce(currentValue, (draft) => {

                    // Increment the "skip" value base on the "limit"
                    draft.skip += draft.limit;

                    // for EACH metricsDefinition, increment the "skip" value for the "pagination" state associated with the metrics definition
                    metricsDefinitionsInfo.forEach((metricsDefInfo) => {
                        const eventDataArray = draft.eventsDataByMetricsDefId[metricsDefInfo.metricsDefinitionId];
                        const lastEventData = eventDataArray && eventDataArray.length > 0 ? eventDataArray[eventDataArray.length - 1] : undefined;
                        draft.totalByMetricsDefId[metricsDefInfo.metricsDefinitionId] = lastEventData?.total;
                    });
                });
            });
        }
    }, [metricsDefinitionsInfo]);


    /**
     * Check if there are more events to fetch. Because there are multiple metrics definitions, we will need to check the status for EACH
     * metrics definition. If at least 1 of the metrics definition has more events then this will return TRUE. This will only return false
     * when NONE of the metrics definition has more events.
     */
    const hasMoreEvents = useMemo((): boolean => {

        // Check if there are more events to fetch for EACH metrics definition.
        return Object.values(metricsDefinitionsInfo ?? []).some((metricsDefInfo) => {

            // Get the array of event data we fetched for the metrics definition
            const eventDataArray = fetchedEventsData.eventsDataByMetricsDefId[metricsDefInfo.metricsDefinitionId];

            // If we already fetched event data for the metricsDefinition then look at the LAST data we fetched to find out if there are more data.
            // Each eventData has a "skip" and "total" value which we can use to determine if we need to fetch more data.
            if (eventDataArray && eventDataArray.length > 0) {

                const lastEventData = eventDataArray[eventDataArray.length - 1];
                const fetchLimit = fetchedEventsData.limit;

                // skip + fetchLimit less than or equal the total then it means we still have more data to fetch
                if (lastEventData.skip + fetchLimit < lastEventData.total) {
                    return true;
                }
            }

            return false;
        });
    }, [fetchedEventsData.eventsDataByMetricsDefId, fetchedEventsData.limit, metricsDefinitionsInfo]);



    /**
     * Given an Event Record, create and return the component/s that display the record details. This function returns an Array of components
     * because it is possible that some records display more than 1 components.
     *
     * TODO - Implement the logic that create the components based on the event type. For now, it will just display the record's data as JSON
     * string.
     */
    const createEventRecordViewComponents = useCallback((recordInfo: EventRecordInfo): readonly React.ReactNode[] => {

        const eventViewCompSettings = props.customSettings?.eventViewComponentPerMetricsDefinition?.[recordInfo.metricsDefId] ?? props.customSettings?.eventViewComponent;
        const eventViewCompTypeName = typeof eventViewCompSettings === 'string' ? eventViewCompSettings : eventViewCompSettings?.componentType;

        // If "eventViewComponent" is an Object then get the "customSettings" from the object, this is OPTIONAL so it can be undefined
        const viewCompCustomSettings = isObjectLiteral(eventViewCompSettings) ? eventViewCompSettings.customSettings : undefined;

        const viewCompInfo = eventViewCompTypeName ? EntityEventViewComponentCollection.getInstance().getComponent(eventViewCompTypeName) : undefined;

        if (viewCompInfo) {
            const Component = viewCompInfo.componentFunc;
            return [
                <Component
                    className={classes.eventRecordView}
                    projectId={props.projectId}
                    entityId={props.entityId}
                    customSettings={viewCompCustomSettings}
                    metricsDefinitionId={recordInfo.metricsDefId}
                    record={recordInfo.record}
                />,
            ];
        }

        return [
            <Paper className={classes.eventRecordView} elevation={2}>
                <textarea
                    className={classes.genericEventRecordViewContent}
                    value={JSON.stringify(recordInfo.record, undefined, 4)}
                    readOnly
                />
            </Paper>,
        ];
    }, [props.customSettings?.eventViewComponentPerMetricsDefinition, props.customSettings?.eventViewComponent, props.projectId, props.entityId,
        classes.eventRecordView, classes.genericEventRecordViewContent]);


    /**
     * On toggle an event filter. This will add the specified filter to the list of filters if it isn't already added to the list. If it is
     * already in the list, this will remove the filter.
     */
    const onEventsFilterToggled = useCallback((filter: EventFilterOption) => {
        setFetchedEventsData((currentValue) => {
            return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                if (currentValue.filters.includes(filter)) {
                    // If the filter is already existed in the list, remove the filter
                    draft.filters = currentValue.filters.filter((existingFilter) => {
                        return existingFilter !== filter;
                    });
                } else {
                    draft.filters.push(filter);
                }

                // Because filter changed, we will have to reset the pagination and fetched events
                draft.skip = 0;
                // Only change these to a new {} if these objects are NOT empty. If they are empty, continue using the old object.
                // This is to prevent the component from re-rendering or hooks from executing again because the state changed
                draft.totalByMetricsDefId = isEmptyObject(draft.totalByMetricsDefId) ? draft.totalByMetricsDefId : {};
                draft.eventsDataByMetricsDefId = isEmptyObject(draft.eventsDataByMetricsDefId) ? draft.eventsDataByMetricsDefId : {};
            });
        });
    }, []);


    /**
     * On clear selected event filters and show all events
     */
    const onClearEventsFilters = useCallback(() => {
        setFetchedEventsData((currentValue) => {
            return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                draft.filters = [];

                // Because filter changed, we will have to reset the pagination and fetched events
                draft.skip = 0;
                // Only change these to a new {} if these objects are NOT empty. If they are empty, continue using the old object.
                // This is to prevent the component from re-rendering or hooks from executing again because the state changed
                draft.totalByMetricsDefId = isEmptyObject(draft.totalByMetricsDefId) ? draft.totalByMetricsDefId : {};
                draft.eventsDataByMetricsDefId = isEmptyObject(draft.eventsDataByMetricsDefId) ? draft.eventsDataByMetricsDefId : {};
            });
        });
    }, []);



    // If there is a setting for "controls.eventsFilter.options", get the list of filter options that are not disabled
    const visibleEventsFilterOptions = useMemo(() => {
        return props.customSettings?.controlBar?.eventsFilter?.options.filter((option) => {
            return option === SELECT_INPUT_OPTION_DIVIDER || !option.hidden;
        });
    }, [props.customSettings?.controlBar?.eventsFilter?.options]);


    // Check the "visibleEventsFilterOptions" and automatically mark the filter options that has "selected" set to true
    useEffect(() => {

        // Find the options that has selected set to true
        const selectedOptions: EventFilterOption[] = [];
        visibleEventsFilterOptions?.forEach((option) => {
            if (isEventFilterOption(option) && option.selected) {
                selectedOptions.push(option);
            }
        });

        if (selectedOptions.length > 0) {
            setFetchedEventsData((currentValue) => {
                return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                    draft.filters = selectedOptions;

                    // Because filter changed, we will have to reset the pagination and fetched events
                    draft.skip = 0;
                    // Only change these to a new {} if these objects are NOT empty. If they are empty, continue using the old object.
                    // This is to prevent the component from re-rendering or hooks from executing again because the state changed
                    draft.totalByMetricsDefId = isEmptyObject(draft.totalByMetricsDefId) ? draft.totalByMetricsDefId : {};
                    draft.eventsDataByMetricsDefId = isEmptyObject(draft.eventsDataByMetricsDefId) ? draft.eventsDataByMetricsDefId : {};
                });
            });
        }
    }, [visibleEventsFilterOptions]);


    /**
     * Create the Control Bar for the filters.
     */
    const controlBar = useMemo(() => {
        if (props.customSettings?.controlBar) {

            return (
                <Paper
                    className={clsx(classes.controlBar)}
                    elevation={2}
                >
                    {visibleEventsFilterOptions && (
                        <div className={classes.filterOption}>
                            <Typography
                                className={classes.filterOptionFieldLabel}
                                variant="h6"
                            >
                                {trans('label.filter.events')}:
                            </Typography>
                            <Button
                                className={classes.filterOptionToggler}
                                endIcon={<Icon>arrow_drop_down</Icon>}
                                onClick={(event) => {
                                    onEventFilterAnchorElClicked(event);
                                }}
                            >
                                {capitalizeAll(trans('label.select_event', { count: 2 }))}
                            </Button>
                            <Menu
                                getContentAnchorEl={null}
                                anchorEl={eventFilterMenuAnchorEl}
                                anchorOrigin={{
                                    vertical  : 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical  : 'top',
                                    horizontal: 'left',
                                }}
                                keepMounted
                                open={isEventFilterMenuOpened}
                                onClose={onEventFilterMenuClosed}
                            >
                                <MenuItem
                                    key="all_option"
                                    className={classes.filterOptionLabel}
                                    selected={fetchedEventsData.filters.length === 0}
                                    onClick={() => {
                                        if (fetchedEventsData.filters.length > 0) {
                                            onClearEventsFilters();
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        <Checkbox
                                            color="primary"
                                            checked={fetchedEventsData.filters.length === 0}
                                            size="small"
                                        />
                                    </ListItemIcon>
                                    {trans('label.all')}
                                </MenuItem>
                                {visibleEventsFilterOptions.map((option, index) => {
                                    if (isEventFilterOption(option)) {
                                        return (
                                            <MenuItem
                                                key={option.labelKey}
                                                className={classes.filterOptionLabel}
                                                selected={fetchedEventsData.filters.includes(option)}
                                                onClick={() => {
                                                    onEventsFilterToggled(option);
                                                }}
                                            >
                                                <ListItemIcon>
                                                    <Checkbox
                                                        color="primary"
                                                        checked={fetchedEventsData.filters.includes(option)}
                                                        size="small"
                                                    />
                                                </ListItemIcon>
                                                {trans(option.labelKey)}
                                            </MenuItem>
                                        );
                                    }

                                    // If an option has no label or value are treated as dividers
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <Divider key={`divider-${index}`} />
                                    );
                                })}
                            </Menu>
                        </div>
                    )}
                </Paper>
            );
        }
        return undefined;
    }, [classes.controlBar, classes.filterOption, classes.filterOptionFieldLabel, classes.filterOptionLabel, classes.filterOptionToggler,
        eventFilterMenuAnchorEl, fetchedEventsData.filters, isEventFilterMenuOpened, onClearEventsFilters, onEventFilterAnchorElClicked,
        onEventFilterMenuClosed, onEventsFilterToggled, props.customSettings?.controlBar, trans, visibleEventsFilterOptions]);



    return (
        <>
            {selectedEntity && (
                <>
                    {controlBar}

                    {combinedEventsRecords.length > 0 && (
                        <>
                            <ExtGrid
                                className={clsx(props.className, classes.root)}
                                container
                                xs_spacing={1}
                                md_spacing={2}
                                lg_spacing={3}
                            >
                                {combinedEventsRecords.map((record) => {
                                    return createEventRecordViewComponents(record).map((node, index) => {
                                        return (
                                            <Grid
                                                item
                                                key={`${record.record.eventKey}-${record.record.timestamp}-${index.toString()}`}
                                                className={classes.eventRecordViewWrapper}
                                                xs={props.customSettings?.gridSizes?.xs ?? 12}
                                                sm={props.customSettings?.gridSizes?.sm}
                                                md={props.customSettings?.gridSizes?.md}
                                                lg={props.customSettings?.gridSizes?.lg}
                                                xl={props.customSettings?.gridSizes?.xl}
                                            >
                                                {node}
                                            </Grid>
                                        );
                                    });
                                })}
                            </ExtGrid>
                            {/* Add the "Load More" button if there are more events to load */}
                            {hasMoreEvents && (
                                <div className={classes.loadMoreButtonContainer}>
                                    <Button
                                        className={classes.loadMoreButton}
                                        variant="contained"
                                        color="primary"
                                        onClick={fetchMoreEvents}
                                    >
                                        {trans('label.load_more')}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}

            {selectedEntity && (!combinedEventsRecords || combinedEventsRecords.length === 0) && (
                <Paper elevation={2}>
                    <GenericMessageBox
                        title={trans('generic.no_data.title', {
                            term: capitalizeAll(trans('label.event', { count: 2 })),
                        })}
                    />
                </Paper>
            )}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityEventsUIComponent', EntityEventsUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Events_Comp',
    {
        componentFunc          : EntityEventsUIComponent,
        customSettingsValidator: isEntityEventsUIComponentCustomSettings,
    },
);
